import { motion } from 'framer-motion';
import React from 'react';
import { FiCheckCircle, FiLayers, FiTarget, FiZap } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const ProjectServicesSection = () => {
  const services = [
    {
      icon: FiTarget,
      title: "Strategic Planning",
      description: "Comprehensive project roadmaps aligned with business objectives",
      color: "from-purple-500 to-indigo-600"
    },
    {
      icon: FiLayers,
      title: "Resource Management",
      description: "Optimal allocation of talent and technology resources",
      color: "from-blue-500 to-purple-600"
    },
    {
      icon: FiZap,
      title: "Agile Execution",
      description: "Rapid delivery with iterative development methodologies",
      color: "from-indigo-500 to-blue-600"
    },
    {
      icon: FiCheckCircle,
      title: "Quality Assurance",
      description: "Rigorous testing and validation throughout the lifecycle",
      color: "from-blue-600 to-indigo-700"
    }
  ];

  return (
    <section id="project-services" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-0 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4 font-accent tracking-wide"
          >
            End-to-End Solutions
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight"
          >
            Project Services
            <span className="block bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              That Deliver Results
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed"
          >
            From conception to completion, we provide comprehensive project services that ensure 
            your initiatives are delivered on time, within budget, and exceed expectations.
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="group"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full border border-gray-100">
                <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <service.icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 font-heading tracking-wide">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed font-body">{service.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Process Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl p-8 md:p-12 shadow-xl"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4 font-display tracking-tight">Our Project Delivery Process</h3>
            <p className="text-gray-600 text-lg font-body font-light tracking-wide">A proven methodology that ensures success at every stage</p>
          </div>

          <div className="grid md:grid-cols-5 gap-8">
            {[
              { step: "01", title: "Discovery", desc: "Understanding requirements and objectives" },
              { step: "02", title: "Planning", desc: "Strategic roadmap and resource allocation" },
              { step: "03", title: "Execution", desc: "Agile development and implementation" },
              { step: "04", title: "Testing", desc: "Quality assurance and validation" },
              { step: "05", title: "Delivery", desc: "Deployment and knowledge transfer" }
            ].map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center relative"
              >
                {index < 4 && (
                  <div style={{left:130}} className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-blue-200 to-purple-200 z-0"></div>
                )}
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-4 shadow-lg font-display">
                    {phase.step}
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2 font-heading tracking-wide">{phase.title}</h4>
                  <p className="text-sm text-gray-600 font-body">{phase.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <Link to="/contact">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-10 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
          >
            Start Your Project
          </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectServicesSection; 